<template>
    <Card pageTitle="Drawer Retained Float">
        <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_CASH_MANAGEMENT">
            <div class="settings-wrap">
                <p class="description">
                    Configure the global retained cash amount left in each drawer after cash-out.
                </p>

                <div class="field-row">
                    <div class="field-label">Retained Float Amount ($)</div>
                    <CustomInput
                        type="number"
                        :step="0.01"
                        v-model="amountInput"
                        :disabled="!canEdit || saving"
                    />
                </div>

                <div class="field-row checkbox-row">
                    <input
                        id="block-on-shortage"
                        type="checkbox"
                        v-model="blockOnShortage"
                        :disabled="!canEdit || saving"
                    />
                    <label for="block-on-shortage">
                        Block cash-out if counted cash is below retained float amount
                    </label>
                </div>

                <div class="policy-preview">
                    Current policy: keep <strong>${{ formattedAmount }}</strong> in drawer and
                    <strong>{{ blockOnShortage ? "block" : "allow" }}</strong> close on shortage.
                </div>

                <div class="actions">
                    <Button borderOnly class="me-2" @click="loadConfig" :disabled="loading || saving">
                        Reload
                    </Button>
                    <Button v-if="canEdit" @click="saveConfig" :disabled="loading || saving">
                        {{ saving ? "Saving..." : "Save" }}
                    </Button>
                </div>
            </div>
        </AllowPermission>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import { checkHasPermission, PERMISSION_CODES } from "../../store/permission"
import Card from "../../components/UI/Card.vue"
import Button from "../../components/UI/Button.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import CashManagementFetchApi from "../../utils/fetch/cash-management-fetch-api"
import { errMsg, round2 } from "../../utils/common"

export default {
    components: {
        Card,
        Button,
        CustomInput,
        AllowPermission,
    },
    data() {
        return {
            PERMISSION_CODES,
            loading: false,
            saving: false,
            amountInput: "100.00",
            blockOnShortage: true,
        }
    },
    computed: {
        ...mapState("auth", ["user"]),
        canEdit() {
            return checkHasPermission({
                isAdmin: this.user?.isAdmin,
                permissionId: PERMISSION_CODES.CMS_EDIT_CASH_MANAGEMENT,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },
        formattedAmount() {
            const numericAmount = Number(this.amountInput || 0)
            return round2(Number.isFinite(numericAmount) ? numericAmount : 0).toFixed(2)
        },
    },
    async mounted() {
        await this.loadConfig()
    },
    methods: {
        async loadConfig() {
            this.loading = true
            try {
                const config = await CashManagementFetchApi.fetchRetainedFloatConfig()
                this.amountInput = Number(config?.amount || 0).toFixed(2)
                this.blockOnShortage = !!config?.blockOnShortage
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not load retained float config"))
            }
            this.loading = false
        },
        async saveConfig() {
            if (!this.canEdit || this.saving) return

            const amount = Number(this.amountInput)
            if (!Number.isFinite(amount) || amount < 0) {
                this.$toast.error("Retained float amount must be a non-negative number")
                return
            }

            if (Math.abs(round2(amount) - amount) > 1e-9) {
                this.$toast.error("Retained float amount supports up to 2 decimal places")
                return
            }

            this.saving = true
            try {
                const updated = await CashManagementFetchApi.updateRetainedFloatConfig({
                    amount: round2(amount),
                    blockOnShortage: !!this.blockOnShortage,
                })
                this.amountInput = Number(updated?.amount || 0).toFixed(2)
                this.blockOnShortage = !!updated?.blockOnShortage
                this.$toast.success("Retained float config updated")
            } catch (err) {
                this.$toast.error(errMsg(err, "Could not update retained float config"))
            }
            this.saving = false
        },
    },
}
</script>

<style lang="scss" scoped>
.settings-wrap {
    max-width: 640px;
}

.description {
    margin-bottom: 16px;
    color: #5f6368;
}

.field-row {
    margin-bottom: 16px;
}

.field-label {
    font-weight: 600;
    margin-bottom: 6px;
}

.checkbox-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.policy-preview {
    padding: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 16px;
}

.actions {
    display: flex;
    align-items: center;
}
</style>
